import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, ChevronRight } from "lucide-react";
import { useLive } from "@/context/LiveContext";

export const WHATSAPP_DEFAULT = "917411298777";

function WaIcon({ className = "w-7 h-7" }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden fill="currentColor">
      <path d="M16.04 3C9.4 3 4 8.36 4 14.97c0 2.27.64 4.4 1.76 6.2L4 29l8.06-1.72a12.1 12.1 0 0 0 3.98.67C22.68 27.95 28 22.6 28 15.98 28 9.36 22.68 3 16.04 3Zm0 22.8c-1.27 0-2.5-.24-3.65-.7l-.52-.21-4.78 1.02 1.03-4.6-.34-.55a9.7 9.7 0 0 1-1.5-5.2c0-5.37 4.4-9.74 9.8-9.74 5.37 0 9.74 4.37 9.74 9.74 0 5.4-4.37 10.24-9.78 10.24Zm5.36-7.3c-.3-.15-1.74-.86-2.01-.95-.27-.1-.47-.15-.66.15-.2.3-.76.95-.93 1.14-.17.2-.34.22-.64.07-.3-.15-1.24-.46-2.37-1.46-.88-.78-1.47-1.74-1.64-2.03-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.66-1.6-.91-2.19-.24-.57-.48-.5-.66-.5h-.57c-.2 0-.52.08-.79.37-.27.3-1.03 1-1.03 2.45 0 1.44 1.06 2.84 1.2 3.04.15.2 2.08 3.17 5.04 4.45.7.3 1.25.48 1.68.62.7.22 1.35.19 1.85.12.57-.09 1.74-.71 1.99-1.4.24-.68.24-1.27.17-1.4-.07-.12-.27-.2-.57-.34Z"/>
    </svg>
  );
}

/** Prewritten messages; the listing page adds a message about the listing being viewed. */
function messagesFor(pathname) {
  const url = typeof window !== "undefined" ? window.location.href : "https://dartmyvenue.com";
  const list = [];
  if (pathname.startsWith("/property/")) list.push(["Ask about this listing", `Hi DartMyVenue, I'm interested in this listing and would like to know about availability and pricing: ${url}`]);
  list.push(
    ["Find a venue for my event", "Hi DartMyVenue, I'm planning an event and need help finding the right venue. Can you help me?"],
    ["Caterers, decorators & more", "Hi DartMyVenue, I'm looking for caterers / decorators / photographers / anchors / musicians for my event. Please share options."],
    ["List my business", "Hi DartMyVenue, I own a business and want to list it on DartMyVenue. Please share the plans and next steps."],
    ["Help with my account or payment", "Hi DartMyVenue, I need help with my account / subscription payment."],
  );
  return list;
}

/** Floating WhatsApp chat button (bottom-right) with quick prewritten messages. */
export default function WhatsAppButton() {
  const { site } = useLive();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [nudge, setNudge] = useState(false);
  const box = useRef(null);
  const number = String(site?.whatsapp || WHATSAPP_DEFAULT).replace(/\D/g, "") || WHATSAPP_DEFAULT;
  const link = (msg) => `https://wa.me/${number}?text=${encodeURIComponent(msg)}`;

  useEffect(() => { setOpen(false); }, [pathname]);
  // a small "Need help?" bubble once per visit
  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem("dmv_wa_nudge") === "1"; } catch {}
    if (seen) return;
    const a = setTimeout(() => { setNudge(true); try { sessionStorage.setItem("dmv_wa_nudge", "1"); } catch {} }, 9000);
    const b = setTimeout(() => setNudge(false), 17000);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  if (pathname.startsWith("/admin")) return null;
  const go = (msg) => { window.open(link(msg), "_blank", "noopener,noreferrer"); setOpen(false); };

  return (
    <div ref={box} className="fixed z-[75] right-4 md:right-6" style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }} data-testid="whatsapp-widget">
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.97 }} transition={{ type: "spring", damping: 24, stiffness: 300 }}
            className="absolute bottom-[72px] right-0 w-[min(92vw,350px)] rounded-3xl overflow-hidden bg-white shadow-[0_28px_70px_rgba(20,40,30,0.30)] border border-black/5 origin-bottom-right" role="dialog" aria-label="Chat on WhatsApp" data-testid="whatsapp-panel">
            <div className="bg-[#075E54] text-white px-4 py-3.5 flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0"><img src="/logo-mark.png" alt="" className="w-7 h-7 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }}/></span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold leading-tight">DartMyVenue</div>
                <div className="text-[12px] text-white/80 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#25D366]"/> Typically replies within minutes</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Close"><X className="w-4 h-4"/></button>
            </div>
            <div className="px-3.5 py-3 bg-[#ECE5DD] max-h-[52vh] overflow-y-auto">
              <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 text-[14px] text-neutral-800 shadow-sm max-w-[88%]">Namaste! 👋 How can we help? Pick a message or type your own.</div>
              <div className="mt-3 space-y-2">
                {messagesFor(pathname).map(([label, msg], i) => (
                  <button key={label} onClick={() => go(msg)} className="press w-full text-left flex items-center gap-2 bg-white hover:bg-[#f3fff7] border border-[#25D366]/30 rounded-xl px-3 py-2.5 text-[14px] text-[#075E54] font-medium transition-colors" data-testid={`whatsapp-msg-${i}`}>
                    <span className="flex-1">{label}</span><ChevronRight className="w-4 h-4 opacity-60"/>
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) { go(text.trim()); setText(""); } }} className="flex items-center gap-2 p-2.5 bg-white border-t border-black/5">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message" className="flex-1 min-w-0 rounded-full bg-neutral-100 px-4 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#25D366]/40" aria-label="Your message" data-testid="whatsapp-input"/>
              <button type="submit" disabled={!text.trim()} className="w-10 h-10 rounded-full bg-[#25D366] text-white flex items-center justify-center disabled:opacity-40 press" aria-label="Send on WhatsApp"><Send className="w-4 h-4"/></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {nudge && !open && (
          <motion.button initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} onClick={() => { setNudge(false); setOpen(true); }}
            className="absolute right-[70px] bottom-3 whitespace-nowrap bg-white text-[#075E54] text-[13px] font-semibold px-3.5 py-2 rounded-full shadow-[0_10px_30px_rgba(0,0,0,0.15)] border border-black/5">
            Need help? Chat with us
          </motion.button>
        )}
      </AnimatePresence>

      <motion.button onClick={() => { setNudge(false); setOpen((o) => !o); }} aria-label={open ? "Close WhatsApp chat" : "Chat with us on WhatsApp"} aria-expanded={open}
        className="wa-fab relative w-[58px] h-[58px] rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_14px_34px_rgba(37,211,102,0.45)]"
        whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} data-testid="whatsapp-button">
        {!open && <span className="wa-ring" aria-hidden/>}
        <AnimatePresence mode="wait" initial={false}>
          {open
            ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}><X className="w-6 h-6"/></motion.span>
            : <motion.span key="wa" className="wa-wiggle" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.6, opacity: 0 }} transition={{ duration: 0.18 }}><WaIcon/></motion.span>}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
